import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { TEACHER_PLANS } from '../config/teacherPlans';
import { useLayoutContext } from '../components/layout/Layout';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isMobile, styles } = useLayoutContext();

  type HomeServiceCard = {
    title: string;
    description: string;
    icon: string;
    path: string;
  };

  /** 수업·코딩·도구 등 학습 중심 서비스 */
  const learningKitServices: HomeServiceCard[] = [
    {
      title: 'MakecodeArcade',
      description: '게임 개발을 통해 코딩을 배우고\n창의력을 키울 수 있는 플랫폼',
      icon: '🎮',
      path: '/makecodearcade/ArcadeMain'
    },
    {
      title: '마인크래프트 에듀케이션',
      description: '월드, 모드, 코드를 공유하고\n다운로드할 수 있는 공간',
      icon: '⛏️',
      path: '/minecraft-education'
    },
    {
      title: '수업 자료',
      description: '기술 교과에 대한 수업 자료 및\n인사이트를 공유하는 공간',
      icon: '📚',
      path: '/class-materials/materials'
    },
    {
      title: '교사 연수',
      description: '직무연수 안내, 연수 자료,\n실습 페이지를 확인하는 공간',
      icon: '🎓',
      path: '/teacher-training'
    },
    {
      title: '드론 시뮬레이터',
      description: '3D 지도 위에서 드론을 조종해\n볼 수 있는 시뮬레이터',
      icon: '🚁',
      path: '/tools/drone-simulator'
    },
    {
      title: '교육 도구',
      description: '룰렛, 타이머, 선착순 등 다양한 교육 상황에서 활용할 수 있는 도구 제공',
      icon: '🛠️',
      path: '/tools'
    }
  ];

  /** 정보부장·IB 등 교무·행정 업무용 자료 */
  const workMaterialsServices: HomeServiceCard[] = [
    {
      title: '정보부장 공유방',
      description: '정보부장 운영에 필요한 파일, 양식,\n링크를 한 번에 확인하는 공간',
      icon: '🗂️',
      path: '/info-chief'
    },
    {
      title: 'IB 성적처리',
      description: 'IB 디플로마 프로그램 성적처리를 위한\n템플릿과 도구 제공',
      icon: '📊',
      path: '/ib-grading'
    }
  ];

  const homeServiceSections: {
    eyebrow: string;
    title: string;
    services: HomeServiceCard[];
  }[] = [
    { eyebrow: '학습을 위해 지원하는 다양한 서비스', title: 'MARU 러닝킷', services: learningKitServices },
    { eyebrow: '교무·행정 업무에 활용할 수 있는 자료', title: '업무 자료', services: workMaterialsServices }
  ];

  // 교육 가치 데이터
  const educationValues = [
    {
      title: '효율적인 학급 관리',
      description: '학생 출석, 과제 관리, 포인트 시스템 등을 통해\n학습을 효율적으로 관리할 수 있습니다.',
      image: 'https://placehold.co/411x238'
    },
    {
      title: '학생 참여 유도',
      description: '포인트 시스템과 다양한 활동을 통해 학생들의\n적극적인 참여를 유도합니다.',
      image: 'https://placehold.co/410x238'
    },
    {
      title: '실시간 피드백',
      description: '학생들의 활동에 대한 실시간 피드백을 제공하여\n학습 효과를 높입니다.',
      image: 'https://placehold.co/411x238'
    }
  ];

  // 핵심 가치 데이터
  const coreValues = [
    {
      title: '창의적 코딩 교육',
      description: 'MakecodeArcade를 통해 게임 개발을 배우고 창의력과 문제 해결 능력을 키울 수 있습니다.'
    },
    {
      title: '풍부한 수업 자료',
      description: '다양한 수업 자료와 인사이트를 통해 교육의 질을 향상시킬 수 있습니다.'
    },
    {
      title: '커뮤니티 형성',
      description: '다양한 수업 자료와 인사이트를 통해 교육의 질을 향상시킬 수 있습니다.'
    }
  ];

  // MARU 시작하기 버튼 클릭
  const handleStartClick = () => {
                  if (currentUser) {
                    // 로그인된 사용자는 역할에 맞는 페이지로 이동
                    if (['teacher', 'schoolAdmin', 'superAdmin'].includes(currentUser.role)) {
                      navigate('/class/teacher/ClassTeacherHome');
                    } else if (currentUser.role === 'student') {
                      navigate('/class/student/StudentPage');
                    }
                  } else {
      // 로그인되지 않은 사용자는 로그인 페이지로
      navigate('/login');
    }
  };

  return (
    <main
      style={{
        width: '100%',
        maxWidth: '100%',
        margin: '0 auto',
        position: 'relative',
        background: 'white',
        paddingLeft: isMobile ? styles.mobile.padding.horizontal : '0',
        paddingRight: isMobile ? styles.mobile.padding.horizontal : '0',
        paddingTop: isMobile ? styles.mobile.padding.top : '0',
        paddingBottom: isMobile ? styles.mobile.padding.bottom : '0',
        boxSizing: 'border-box'
      }}
    >
        {/* 히어로 섹션 */}
        <div style={{
          width: '100%',
          maxWidth: '1440px',
          margin: '0 auto',
          height: isMobile ? 'auto' : '594px',
          minHeight: isMobile ? '400px' : '594px',
          position: 'relative',
          overflow: 'hidden',
          paddingTop: isMobile ? '0' : '0',
          paddingBottom: isMobile ? '40px' : '0',
          paddingLeft: isMobile ? '0' : '80px',
          paddingRight: isMobile ? '0' : '80px',
          boxSizing: 'border-box'
        }}>
          {/* 배경 이미지 */}
          <img 
            style={{
              width: isMobile ? '100%' : '1137.82px',
              height: isMobile ? 'auto' : '595px',
              position: isMobile ? 'relative' : 'absolute',
              left: isMobile ? 'auto' : '304px',
              top: isMobile ? 'auto' : 0,
              objectFit: 'cover',
              borderRadius: isMobile ? '12px' : '0',
              marginBottom: isMobile ? '20px' : '0'
            }} 
            src="/images/Maru.png" 
            alt="MARU 교육 플랫폼"
          />
          
          {/* 그라디언트 오버레이 */}
          {!isMobile && (
            <div style={{
              width: '100%',
              maxWidth: '1440px',
              height: '595px',
              left: 0,
              top: 0,
              position: 'absolute',
              background: 'linear-gradient(270deg, rgba(255, 255, 255, 0) 0%, white 100%)',
              backdropFilter: 'blur(0px)'
            }} />
          )}

          {/* 메인 타이틀 및 설명 */}
          <div style={{
            width: isMobile ? '100%' : '463px',
            left: isMobile ? 'auto' : '80px',
            top: isMobile ? 'auto' : '87px',
            position: isMobile ? 'relative' : 'absolute',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: isMobile ? '12px' : '16px',
            display: 'inline-flex',
            zIndex: 10,
            padding: '0'
          }}>
            <div style={{
              width: isMobile ? '100%' : '318px',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              display: 'flex'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                color: 'black',
                fontSize: isMobile ? '28px' : '44px',
                fontFamily: 'pretendard',
                fontWeight: '600',
                whiteSpace: 'nowrap',
                lineHeight: isMobile ? '42px' : '66px'
              }}>
                함께 성장하는 교실
              </div>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                color: 'black',
                fontSize: isMobile ? '28px' : '44px',
                fontFamily: 'pretendard',
                fontWeight: '600',
                lineHeight: isMobile ? '42px' : '66px'
              }}>
                함께 배우는 미래
              </div>
            </div>
            <div style={{
              color: 'var(--Color-text-assistive-strong, #4C4C4C)',
              fontSize: isMobile ? '14px' : '18px',
              fontFamily: 'pretendard',
              fontWeight: '400',
              lineHeight: isMobile ? '21px' : '27px',
              whiteSpace: 'pre-line'
            }}>
              교사와 학생이 함께 사용하며 수업을 더 편리하고{'\n'}즐겁게 만들어 소통을 원활하게 하고{'\n'}참여도를 높여 학급의 배움을 확장하는 경험을 지금 만나보세요!
            </div>
          </div>

          {/* MARU 시작하기 버튼 */}
          <div style={{
            width: isMobile ? 'calc(100% - 40px)' : '160px',
            left: isMobile ? 'auto' : '80px',
            top: isMobile ? 'auto' : '433px',
            position: isMobile ? 'relative' : 'absolute',
            zIndex: 10,
            marginTop: isMobile ? '24px' : '0',
            marginLeft: isMobile ? '0' : '0'
          }}>
            <div 
              style={{
                padding: '16px 20px',
                background: 'var(--Color-fill-brand-color, #FF6F0F)',
                borderRadius: '12px',
                          display: 'flex',
                          justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
                width: isMobile ? '100%' : 'auto'
              }}
              onClick={handleStartClick}
            >
              <div style={{
                color: 'var(--Color-text-inverse, white)',
                fontSize: isMobile ? '13px' : '14px',
                fontFamily: 'pretendard',
                fontWeight: '500',
                lineHeight: '21px'
              }}>
                MARU 시작하기
              </div>
            </div>
          </div>
        </div>

        <section
          style={{
            width: '100%',
            maxWidth: '1440px',
            margin: '0 auto',
            padding: isMobile ? '48px 0 0' : '80px 80px 0',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
          }}
        >
          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--Color-fill-brand-color, #FF6F0F)' }}>
            서비스 오픈
          </div>
          <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--Color-text-strong, #0D0D0D)' }}>
            교사 요금제에 따라 학급 기능이 열립니다
          </div>
          <div style={{ fontSize: '16px', lineHeight: '24px', color: 'var(--Color-text-normal, #161616)', maxWidth: '640px' }}>
            글 공지는 데이터베이스에만 남습니다. 사진, PDF, 도장 이미지는 Firebase 저장소에 올라가 용량과 다운로드 요금이 생기므로 학급 요금제부터 사용합니다.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '16px' }}>
            {TEACHER_PLANS.map((plan) => (
              <div
                key={plan.id}
                style={{
                  padding: '20px',
                  borderRadius: '16px',
                  border: '1px solid var(--Color-line-normal, #F0F0F0)',
                  background: 'var(--Color-background-normal, white)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                <div style={{ fontSize: '20px', fontWeight: 600 }}>{plan.name}</div>
                <div style={{ fontSize: '14px', lineHeight: '21px', color: 'var(--Color-text-normal, #161616)' }}>{plan.summary}</div>
                <div style={{ fontSize: '13px', lineHeight: '20px', color: 'var(--Color-text-strong, #0D0D0D)' }}>
                  {plan.features.join(' · ')}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* MARU 러닝킷 / 업무 자료 섹션 */}
        {homeServiceSections.map((section, sectionIndex) => (
          <div
            key={section.title}
            style={{
              width: '100%',
              maxWidth: '100%',
              paddingLeft: isMobile ? '0' : '80px',
              paddingRight: isMobile ? '0' : '80px',
              paddingTop:
                sectionIndex === 0
                  ? isMobile
                    ? styles.mobile.padding.top
                    : '112px'
                  : isMobile
                    ? '48px'
                    : '80px',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              gap: isMobile ? '24px' : '40px',
              display: 'flex',
              margin: '0 auto',
              boxSizing: 'border-box'
            }}
          >
            {/* 섹션 타이틀 */}
            <div
              style={{
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: '4px',
                display: 'flex'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: '20px',
                  fontFamily: 'pretendard',
                  fontWeight: '500',
                  lineHeight: '30px'
                }}
              >
                {section.eyebrow}
              </div>
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: isMobile ? '24px' : '36px',
                  fontFamily: 'pretendard',
                  fontWeight: '600',
                  lineHeight: isMobile ? '36px' : '54px',
                  paddingLeft: '0',
                  paddingRight: '0'
                }}
              >
                {section.title}
              </div>
            </div>

            {/* 서비스 카드 그리드 */}
            <div
              style={{
                display: 'flex',
                justifyContent: isMobile ? 'center' : 'flex-start',
                alignItems: 'flex-start',
                gap: isMobile ? '16px' : '24px',
                flexWrap: 'wrap',
                paddingLeft: '0',
                paddingRight: '0',
                width: '100%'
              }}
            >
            {section.services.map((service) => (
              <div 
                key={service.path}
                style={{
                  width: isMobile ? '100%' : '302px',
                  minWidth: isMobile ? '100%' : '302px',
                  height: isMobile ? 'auto' : '305px',
                  minHeight: isMobile ? 'auto' : '305px',
                  padding: isMobile ? '16px 20px' : '32px',
                  background: 'var(--Color-background-sub, #F8F8F8)',
                  borderRadius: '12px',
                  display: 'flex',
                  flexDirection: isMobile ? 'row' : 'column',
                  justifyContent: isMobile ? 'flex-start' : 'flex-start',
                  alignItems: isMobile ? 'center' : 'flex-end',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease',
                  boxSizing: 'border-box',
                  gap: isMobile ? '16px' : '20px'
                }}
                onClick={() => navigate(service.path)}
                onMouseEnter={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'translateY(0)';
                  }
                }}
              >
                {/* 아이콘 */}
                <div style={{
                  width: isMobile ? '48px' : '90px',
                  height: isMobile ? '48px' : '90px',
                  minWidth: isMobile ? '48px' : '90px',
                  background: 'var(--Color-fill-white, white)',
                  borderRadius: '9999px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isMobile ? '24px' : '48px',
                  flexShrink: 0
                }}>
                  {service.icon}
                </div>
                
                {/* 제목 및 링크 */}
                <div style={{
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: isMobile ? 'flex-start' : 'flex-start',
                  gap: isMobile ? '4px' : '24px',
                  display: 'flex',
                  flex: 1,
                  width: '100%'
                }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: isMobile ? '16px' : '18px',
                    fontFamily: 'pretendard',
                    fontWeight: '600',
                    lineHeight: isMobile ? '24px' : '27px'
                  }}>
                    {service.title}
                  </div>
                  
                  {/* 설명 - 데스크톱에서만 표시 */}
                  {!isMobile && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                      gap: '8px',
                      width: '100%'
                    }}>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                        fontSize: '16px',
                        fontFamily: 'pretendard',
                        fontWeight: '500',
                        lineHeight: '24px',
                        whiteSpace: 'pre-line'
                      }}>
                        {service.description}
                      </div>
                      
                      {/* 바로가기 링크 */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        gap: '2px'
                      }}>
                        <div style={{
                          color: 'var(--Color-text-brand-color, #FF6F0F)',
                          fontSize: '16px',
                          fontFamily: 'pretendard',
                          fontWeight: '500',
                          lineHeight: '24px'
                        }}>
                          바로가기
                        </div>
                        <div style={{
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          justifyContent: 'flex-start',
                          alignItems: 'center'
                        }}>
                          <div style={{
                            width: '4.33px',
                            height: '7.67px',
                            background: 'var(--Color-text-brand-color, #FF6F0F)'
                          }} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            </div>
          </div>
        ))}

        {/* 교육 가치 섹션 */}
        <div style={{
          width: '100%',
          maxWidth: '100%',
          paddingLeft: isMobile ? '0' : '80px',
          paddingRight: isMobile ? '0' : '80px',
          paddingTop: isMobile ? styles.mobile.padding.top : '235px',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          gap: isMobile ? '24px' : '40px',
          display: 'flex',
          margin: '0 auto',
          boxSizing: 'border-box'
        }}>
          {/* 섹션 타이틀 */}
          <div style={{
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: '4px',
            display: 'flex',
            width: '100%',
            paddingLeft: '0',
            paddingRight: '0'
          }}>
            <div style={{
              textAlign: isMobile ? 'left' : 'center',
              display: 'flex',
              flexDirection: 'column',
                          justifyContent: 'center',
              color: 'var(--Color-text-normal, #161616)',
              fontSize: isMobile ? '14px' : '20px',
              fontFamily: 'pretendard',
              fontWeight: '500',
              lineHeight: isMobile ? '21px' : '30px'
            }}>
              마루는 교사와 학생 모두의 성장을 위해
            </div>
            <div style={{
              width: '100%',
              textAlign: isMobile ? 'left' : 'center',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              color: 'var(--Color-text-normal, #161616)',
              fontSize: isMobile ? '20px' : '36px',
              fontFamily: 'pretendard',
              fontWeight: '600',
              lineHeight: isMobile ? '30px' : '54px'
            }}>
              관리의 효율, 참여의 즐거움이 공존하는 교실을 만듭니다.
            </div>
          </div>

          {/* 교육 가치 카드들 */}
          <div style={{
            display: 'flex',
            justifyContent: isMobile ? 'center' : 'flex-start',
            alignItems: 'center',
            gap: isMobile ? '16px' : '24px',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            width: '100%',
            paddingLeft: '0',
            paddingRight: '0'
          }}>
            {educationValues.map((value, index) => (
              <div 
                key={index}
                style={{
                  width: isMobile ? '100%' : (index === 1 ? '410px' : '411px'),
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: isMobile ? 'center' : (index === 1 ? 'flex-end' : 'flex-start'),
                  gap: isMobile ? '12px' : '20px',
                  display: 'inline-flex'
                }}
              >
                {index === 0 ? (
                  <img 
                    style={{
                      width: '100%',
                      height: isMobile ? '180px' : '238px',
                      objectFit: 'cover',
                      borderRadius: '12px'
                    }} 
                    src={value.image}
                    alt={value.title}
                  />
                ) : (
                  <img 
                    style={{
                      height: isMobile ? '180px' : '238px',
                      borderRadius: '12px',
                      width: '100%',
                      objectFit: 'cover'
                    }} 
                    src={value.image}
                    alt={value.title}
                  />
                )}
                
                <div style={{
                  height: isMobile ? 'auto' : '83px',
                  minHeight: isMobile ? '60px' : '83px',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: isMobile ? 'center' : 'flex-start',
                  gap: isMobile ? '6px' : '8px',
                  display: 'flex',
                  width: '100%',
                  textAlign: isMobile ? 'center' : 'left'
                }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: isMobile ? '16px' : '18px',
                    fontFamily: 'pretendard',
                    fontWeight: '600',
                    lineHeight: isMobile ? '24px' : '27px'
                  }}>
                    {value.title}
                  </div>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                    fontSize: isMobile ? '14px' : '16px',
                    fontFamily: 'pretendard',
                    fontWeight: '500',
                    lineHeight: isMobile ? '21px' : '24px',
                    whiteSpace: 'pre-line'
                  }}>
                    {value.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 모두가 함께하는 교육 섹션 */}
        <div style={{
          width: '100%',
          maxWidth: '100%',
          paddingLeft: isMobile ? '0' : '80px',
          paddingRight: isMobile ? '0' : '80px',
          paddingTop: isMobile ? styles.mobile.padding.top : '235px',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          gap: isMobile ? '20px' : '24px',
          margin: '0 auto',
          boxSizing: 'border-box'
        }}>
          {/* 좌측 타이틀 */}
          <div style={{
            width: isMobile ? '100%' : '411px',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: isMobile ? 'center' : 'flex-start',
            gap: '4px',
            display: 'inline-flex',
            paddingLeft: '0',
            paddingRight: '0'
          }}>
            <div style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              color: 'var(--Color-text-normal, #161616)',
              fontSize: isMobile ? '20px' : '32px',
              fontFamily: 'pretendard',
              fontWeight: '600',
              lineHeight: isMobile ? '30px' : '48px',
              textAlign: isMobile ? 'center' : 'left'
            }}>
              모두가 함께하는 교육
            </div>
          </div>

          {/* 우측 핵심 가치 리스트 */}
          <div style={{
            width: isMobile ? '100%' : '845px',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: isMobile ? '16px' : '25px',
            display: 'inline-flex',
            paddingLeft: '0',
            paddingRight: '0'
          }}>
            {coreValues.map((value, index) => (
              <div 
                key={index}
                style={{
                  height: isMobile ? 'auto' : (index === 1 ? '81px' : '82px'),
                  minHeight: isMobile ? '100px' : (index === 1 ? '81px' : '82px'),
                  padding: isMobile ? '16px' : '11px 48px',
                  background: 'var(--Color-background-main, #F4F6FA)',
                  borderRadius: '12px',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  gap: isMobile ? '8px' : '10px',
                  display: 'flex',
                  width: '100%'
                }}
              >
                <div style={{
                  width: '100%',
                  flex: '1',
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  justifyContent: 'space-between',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  gap: isMobile ? '8px' : '0'
                }}>
                  <div style={{
                    width: isMobile ? '100%' : '120px',
                    textAlign: isMobile ? 'left' : 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: isMobile ? '16px' : '18px',
                    fontFamily: 'pretendard',
                    fontWeight: '600',
                    lineHeight: isMobile ? '24px' : '27px'
                  }}>
                    {value.title}
                  </div>
                  <div style={{
                    width: isMobile ? '100%' : (index === 1 || index === 2 ? '577px' : 'auto'),
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    textAlign: isMobile ? 'left' : (index === 0 ? 'center' : 'left'),
                    color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                    fontSize: isMobile ? '14px' : '16px',
                    fontFamily: 'pretendard',
                    fontWeight: '500',
                    lineHeight: isMobile ? '21px' : '24px'
                  }}>
                    {value.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          width: '100%',
          maxWidth: '100%',
          height: isMobile ? 'auto' : '157px',
          minHeight: isMobile ? '200px' : '157px',
          padding: isMobile ? `32px ${styles.mobile.padding.horizontal}` : '32px 80px',
          marginTop: isMobile ? styles.mobile.padding.top : '235px',
          marginLeft: isMobile ? `calc(-1 * ${styles.mobile.padding.horizontal})` : '0',
          marginRight: isMobile ? `calc(-1 * ${styles.mobile.padding.horizontal})` : '0',
          background: 'var(--Color-fill-normal, #293044)',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: isMobile ? '16px' : '8px',
          display: 'flex',
          boxSizing: 'border-box'
        }}>
          <div style={{
            width: isMobile ? '100%' : '363px',
            maxWidth: '363px',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: isMobile ? '16px' : '20px',
            display: 'flex'
          }}>
            <div style={{
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '20px',
              display: 'flex'
            }}>
              {/* 회사 정보 */}
              <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'center',
                alignItems: 'center',
                gap: isMobile ? '8px' : '10px',
                flexWrap: 'wrap'
              }}>
                <div style={{
                  height: isMobile ? 'auto' : '12px',
                  paddingRight: isMobile ? '0' : '10px',
                  borderRight: isMobile ? 'none' : '1px solid var(--Color-text-assistive, #989898)',
                  borderBottom: isMobile ? 'none' : 'none',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    color: 'var(--Color-text-assistive, #989898)',
                    fontSize: isMobile ? '11px' : '12px',
                    fontFamily: 'pretendard',
                    fontWeight: '500',
                    lineHeight: isMobile ? '16px' : '18px'
                  }}>
                    이름: 한지호 교사 (서울용강중학교)
                  </div>
                </div>
                <div style={{
                  height: isMobile ? 'auto' : '12px',
                  paddingRight: isMobile ? '0' : '10px',
                  borderRight: isMobile ? 'none' : '1px solid var(--Color-text-assistive, #989898)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    color: 'var(--Color-text-assistive, #989898)',
                    fontSize: isMobile ? '11px' : '12px',
                    fontFamily: 'pretendard',
                    fontWeight: '500',
                    lineHeight: isMobile ? '16px' : '18px'
                  }}>
                    <a href="mailto:jihotech16@outlook.kr" style={{ color: 'inherit', textDecoration: 'underline' }}>
                      이메일: jihotech16@outlook.kr
                    </a>
                  </div>
                </div>
                <div style={{
                  height: isMobile ? 'auto' : '12px',
                  paddingRight: isMobile ? '0' : '10px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '6px',
                    color: 'var(--Color-text-assistive, #989898)',
                    fontSize: isMobile ? '11px' : '12px',
                    fontFamily: 'pretendard',
                    fontWeight: '500',
                    lineHeight: isMobile ? '16px' : '18px'
                  }}>
                    <a href="tel:010-4905-0554" style={{ color: 'inherit', textDecoration: 'underline' }}>
                      문의: 010-4905-0554
                    </a>
                  </div>
                </div>
              </div>
              
              {/* 약관 링크 */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: '16px'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  color: 'var(--Color-text-assistive, #989898)',
                  fontSize: '12px',
                  fontFamily: 'pretendard',
                  fontWeight: '500',
                  lineHeight: '18px',
                  cursor: 'pointer'
                }}>
                  이용약관
                </div>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  color: 'var(--Color-text-minor, #BABABA)',
                  fontSize: '12px',
                  fontFamily: 'pretendard',
                  fontWeight: '600',
                  lineHeight: '18px',
                  cursor: 'pointer'
                }}>
                  개인정보처리방침
                </div>
              </div>
            </div>
            
            {/* 저작권 */}
            <div style={{
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              color: 'var(--Color-text-assistive, #989898)',
              fontSize: '12px',
              fontFamily: 'pretendard',
              fontWeight: '500',
              lineHeight: '18px'
            }}>
              @Maru Corp. All rights reserved.
            </div>
          </div>
        </div>
      </main>
  );
};

export default Home; 
